/**
 * Global `keydown` handler for canvas editing: shortcuts defer to `canEdit` from
 * `useCanvasPermissions`. Registers once per mount with a ref to latest handlers from `CanvasPage`.
 */

import {
  clearInsertSource,
  markInsertSource,
} from "@/pages/canvas/analytics/insertSource";
import {
  collectFileEntityIds,
  deleteFileRowsForRemovedNodes,
} from "@/pages/canvas/utils/boardFileDelete";
import { trackCanvasFitView, trackCanvasShortcutUsed } from "@/utils/analytics";
import { isEscapeOwnedByOverlay } from "@/utils/overlayOwnsEscape";
import { isBlockNodeData } from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { useReactFlow, useStoreApi, type Node } from "@xyflow/react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { armsNonCommentTool, CANVAS_KEYS } from "../canvas-controls.config";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import { useCanvasUi, type HtmlBlockFocus } from "../context/CanvasUiContext";
import { isNodeLocked } from "../utils/nodeLock";
import { buildDetachUpdatesForSectionRemovals } from "../utils/sectionMembership";
import { useCanvasPermissions } from "./useCanvasPermissions";
import type { CopyPasteControls } from "./useCopyPaste";

export interface CanvasKeyboardShortcutsOptions {
  canvasId: string | undefined;
  lastCursorScreenPosRef: React.RefObject<{ x: number; y: number } | null>;
  canEdit: boolean;
  canComment: boolean;
  /** Arms comment placement; the next pane click drops the pin. */
  onStartComment: () => void;
  /** Disarms it again — every other tool key calls this before arming. */
  onCancelComment: () => void;
  quickChat: {
    handleStartQuickChat: (
      pos: { x: number; y: number },
      source?: string,
    ) => void;
    handleStartQuickVoice: (
      pos: { x: number; y: number },
      source?: string,
    ) => void;
    composingState?: { mode?: string } | null;
    handleCancelComposing: () => void;
  };
  copyPaste: CopyPasteControls;
  undo: () => void;
  redo: () => void;
  canvas: {
    removeNodes: (ids: string[]) => void;
    removeEdges: (ids: string[]) => void;
    batchUpdateNodeParents: (
      updates: Array<{
        nodeId: string;
        parentId: null;
        position: { x: number; y: number };
      }>,
    ) => void;
    addNode: (node: any) => void;
    expandNode: (nodeId: string) => void;
  };
  /** A block the user is inside (interact / edit) owns Enter for its content. */
  blockFocus: HtmlBlockFocus | null;
  isPlacingTextLabel: boolean;
  isPlacingTextNote: boolean;
  isPlacingDoc: boolean;
  /** Any tool armed — placement of any kind, or the pen. */
  isPlacing: boolean;
  setIsPlacingTextLabel: (v: boolean) => void;
  setIsPlacingTextNote: (v: boolean) => void;
  setIsPlacingDoc: (v: boolean) => void;
  handleToggleTextLabelMode: () => void;
  handleToggleTextNoteMode: () => void;
  handleToggleDocMode: () => void;
  cancelPlacingShape: () => void;
  cancelPlacingConnector: () => void;
  cancelDrawing: () => void;
  cancelPlacingSection: () => void;
  handleToggleSectionMode: () => void;
  setIsWidgetsMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  setIsLinkMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  setIsShapesMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  isWidgetsMenuOpen: boolean;
  /**
   * Whether P arms the last shape as it opens the Shapes menu. Main's
   * behaviour (true): cancel any shape or connector and arm the last shape.
   * The page passes false under the redrawn bar while a shape or a
   * connector is armed (D31); this hook does not read the flag.
   */
  shapesKeyArmsLastShape?: boolean;
  isLinkMenuOpen: boolean;
  lastPlacedShape: string;
  startPlacingShape: (shape: string) => void;
  onToggleExplorer: () => void;
  onInsertNewNote: () => void;
  onInsertNewPage: () => void;
  onInsertUploadFile: () => void;
}

export type CanvasKeyboardShortcutsOptionsInput = Omit<
  CanvasKeyboardShortcutsOptions,
  | "canvasId"
  | "canEdit"
  | "canComment"
  | "canvas"
  | "blockFocus"
  | "setIsWidgetsMenuOpen"
  | "setIsLinkMenuOpen"
  | "setIsShapesMenuOpen"
  | "isWidgetsMenuOpen"
  | "isLinkMenuOpen"
>;

/**
 * Registers global keydown handler for canvas shortcuts (quick chat, voice,
 * placement modes, shapes, widgets, undo/redo, zoom, paste, copy/cut/duplicate, delete).
 * Uses a ref to read the latest options so the listener is registered once.
 * `canEdit` comes from `useCanvasPermissions()`; React Flow from `useReactFlow()`.
 */
export function useCanvasKeyboardShortcuts(
  options: CanvasKeyboardShortcutsOptionsInput,
) {
  const { canvasId } = useCanvasRoute();
  const ctx = useCanvas();
  const location = useLocation();
  const {
    setCursorTool,
    setIsWidgetsMenuOpen,
    setIsLinkMenuOpen,
    setIsShapesMenuOpen,
    isWidgetsMenuOpen,
    isLinkMenuOpen,
    blockFocus,
  } = useCanvasUi();
  const { canEdit, canComment } = useCanvasPermissions();
  const reactFlowInstance = useReactFlow();
  const reactFlowRef = useRef(reactFlowInstance);
  reactFlowRef.current = reactFlowInstance;
  const storeApi = useStoreApi();

  const navRef = useRef({
    location,
    canvasId,
  });
  navRef.current = {
    location,
    canvasId,
  };

  // Two-meaning delete needs the query cache — ref-backed like optsRef so
  // the stable keydown closure reads fresh values.
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const canvas = {
    removeNodes: ctx.removeNodes,
    removeEdges: ctx.removeEdges,
    batchUpdateNodeParents: ctx.batchUpdateNodeParents,
    addNode: ctx.addNode,
    expandNode: ctx.expandNode,
  };
  const optsRef = useRef<CanvasKeyboardShortcutsOptions>({
    ...options,
    canEdit,
    canComment,
    canvas,
    canvasId,
    blockFocus,
    setIsWidgetsMenuOpen,
    setIsLinkMenuOpen,
    setIsShapesMenuOpen,
    isWidgetsMenuOpen,
    isLinkMenuOpen,
  });
  optsRef.current = {
    ...options,
    canEdit,
    canComment,
    canvas,
    canvasId,
    blockFocus,
    setIsWidgetsMenuOpen,
    setIsLinkMenuOpen,
    setIsShapesMenuOpen,
    isWidgetsMenuOpen,
    isLinkMenuOpen,
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const opts = optsRef.current;
      const {
        canEdit,
        canComment,
        quickChat,
        copyPaste,
        undo,
        redo,
        canvas,
        lastCursorScreenPosRef,
      } = opts;
      const rf = reactFlowRef.current;

      const defaultPos = lastCursorScreenPosRef.current ?? {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      };

      if (
        event.key === CANVAS_KEYS.comment &&
        !event.ctrlKey &&
        !event.metaKey &&
        canComment
      ) {
        event.preventDefault();
        opts.onStartComment();
        return;
      }

      // Everything below arms a tool of its own, and the comment overlay owns
      // every board click while it is up. Disarming here rather than in each
      // branch keeps the next key added from reopening the same hole; the
      // predicate carries the branches' own gates so a press that arms nothing
      // — no edit rights, shifted, uppercase — cancels nothing either.
      if (armsNonCommentTool(event, { canEdit })) opts.onCancelComment();

      if (
        event.key === CANVAS_KEYS.quickChat &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "quickChat" });
        quickChat.handleStartQuickChat(defaultPos, "keyboard");
        return;
      }

      // Hand: a cursor mode, not a placement, so neither canEdit nor the
      // other tools are touched — the pointer just grabs the board.
      if (
        event.key.toLowerCase() === CANVAS_KEYS.hand &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "hand" });
        setCursorTool("hand");
        return;
      }

      if (
        event.key === CANVAS_KEYS.voiceInput &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "voiceInput" });
        quickChat.handleStartQuickVoice(defaultPos, "keyboard");
        return;
      }

      if (
        event.key === CANVAS_KEYS.addTextLabel &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "addTextLabel" });
        markInsertSource("keyboard");
        if (opts.isPlacingTextNote) opts.setIsPlacingTextNote(false);
        if (opts.isPlacingDoc) opts.setIsPlacingDoc(false);
        opts.cancelPlacingSection();
        opts.handleToggleTextLabelMode();
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.addNote &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "addNote" });
        markInsertSource("keyboard");
        opts.onInsertNewNote();
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.addLink &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "addLink" });
        markInsertSource("keyboard");
        if (opts.isLinkMenuOpen) {
          opts.setIsLinkMenuOpen(false);
        } else {
          opts.setIsPlacingTextNote(false);
          opts.setIsPlacingTextLabel(false);
          opts.setIsPlacingDoc(false);
          opts.cancelPlacingShape();
          opts.cancelPlacingConnector();
          opts.cancelDrawing();
          opts.cancelPlacingSection();
          opts.setIsShapesMenuOpen(false);
          opts.setIsWidgetsMenuOpen(false);
          opts.setIsLinkMenuOpen(true);
        }
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.uploadFile &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "uploadFile" });
        markInsertSource("keyboard");
        opts.onInsertUploadFile();
        return;
      }

      if (
        event.key === CANVAS_KEYS.addDoc &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "addDoc" });
        markInsertSource("keyboard");
        opts.onInsertNewPage();
        return;
      }

      if (
        event.key === CANVAS_KEYS.toggleExplorer &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "toggleExplorer" });
        if (opts.isPlacingTextNote) opts.setIsPlacingTextNote(false);
        if (opts.isPlacingTextLabel) opts.setIsPlacingTextLabel(false);
        if (opts.isPlacingDoc) opts.setIsPlacingDoc(false);
        opts.cancelPlacingShape();
        opts.cancelPlacingConnector();
        opts.cancelDrawing();
        opts.cancelPlacingSection();
        opts.setIsWidgetsMenuOpen(false);
        opts.setIsLinkMenuOpen(false);
        opts.onToggleExplorer();
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.addSection &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "addSection" });
        markInsertSource("keyboard");
        if (opts.isPlacingTextNote) opts.setIsPlacingTextNote(false);
        if (opts.isPlacingTextLabel) opts.setIsPlacingTextLabel(false);
        if (opts.isPlacingDoc) opts.setIsPlacingDoc(false);
        opts.cancelPlacingShape();
        opts.cancelPlacingConnector();
        opts.cancelDrawing();
        opts.setIsWidgetsMenuOpen(false);
        opts.setIsLinkMenuOpen(false);
        opts.setIsShapesMenuOpen(false);
        opts.handleToggleSectionMode();
        return;
      }

      if (
        event.key === CANVAS_KEYS.addShapes &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "addShapes" });
        markInsertSource("keyboard");
        if (opts.isPlacingTextNote) opts.setIsPlacingTextNote(false);
        if (opts.isPlacingTextLabel) opts.setIsPlacingTextLabel(false);
        if (opts.isPlacingDoc) opts.setIsPlacingDoc(false);
        const armsLastShape = opts.shapesKeyArmsLastShape ?? true;
        if (armsLastShape) {
          opts.cancelPlacingShape();
          opts.cancelPlacingConnector();
        }
        opts.cancelDrawing();
        opts.cancelPlacingSection();
        opts.setIsWidgetsMenuOpen(false);
        opts.setIsLinkMenuOpen(false);
        opts.setIsShapesMenuOpen((prev) => {
          if (!prev && armsLastShape) {
            opts.startPlacingShape(opts.lastPlacedShape);
          }
          return !prev;
        });
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.addWidgets &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "addWidgets" });
        markInsertSource("keyboard");
        if (opts.isWidgetsMenuOpen) {
          opts.setIsWidgetsMenuOpen(false);
        } else {
          opts.setIsPlacingTextNote(false);
          opts.setIsPlacingTextLabel(false);
          opts.setIsPlacingDoc(false);
          opts.cancelPlacingShape();
          opts.cancelPlacingConnector();
          opts.cancelDrawing();
          opts.cancelPlacingSection();
          opts.setIsShapesMenuOpen(false);
          opts.setIsLinkMenuOpen(false);
          opts.setIsWidgetsMenuOpen(true);
        }
        return;
      }

      // Enter opens the selected block full-size (the lightbox) — the
      // keyboard route beside the toolbar, the node menu and the pane
      // header. Exactly one selected node, and it must be a block; a user
      // already inside a block (interact / edit) owns Enter for its content.
      // Viewers expand too, so this sits ahead of the canEdit gate.
      if (
        event.key === "Enter" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.shiftKey &&
        opts.blockFocus === null
      ) {
        const selectedNodes = rf.getNodes().filter((n) => n.selected);
        const only = selectedNodes.length === 1 ? selectedNodes[0] : undefined;
        if (only && isBlockNodeData(only.data)) {
          event.preventDefault();
          trackCanvasShortcutUsed({ shortcut: "expandNode" });
          canvas.expandNode(only.id);
          return;
        }
      }

      if (event.key === CANVAS_KEYS.cancel) {
        // Escape belongs to the innermost thing on screen; clearing the
        // selection is what is left when nothing else claims it. An open menu
        // or popover is claim enough — a selector without a search field
        // leaves focus on the popover itself, which the editable-target guard
        // above reads as a plain element — so the first Escape closes the menu
        // and the second clears the selection. Claim is read off the DOM, not
        // `defaultPrevented`: every Radix layer preventDefaults the press it
        // consumes, and an open tooltip is one of those — a cursor resting on
        // a toolbar button would otherwise cost every Escape.
        if (isEscapeOwnedByOverlay(event)) return;

        if (quickChat.composingState) {
          clearInsertSource();
          quickChat.handleCancelComposing();
          return;
        }

        // The placement hooks and the block-focus latch listen on window too
        // and cancel themselves on this same press; cancelling a tool or
        // stepping out of a block must not also cost the selection.
        if (opts.isPlacing || opts.blockFocus !== null) return;

        trackCanvasShortcutUsed({ shortcut: "cancel" });
        storeApi.getState().unselectNodesAndEdges();
        return;
      }

      if (
        event.key === CANVAS_KEYS.undo &&
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "undo" });
        undo();
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.redo &&
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        canEdit
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "redo" });
        redo();
        return;
      }

      if (
        event.key === CANVAS_KEYS.zoomIn &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "zoomIn" });
        rf.zoomIn({ duration: 200 });
        return;
      }
      if (
        event.key === CANVAS_KEYS.zoomOut &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "zoomOut" });
        rf.zoomOut({ duration: 200 });
        return;
      }
      if (
        event.key === CANVAS_KEYS.zoomToFit &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "zoomToFit" });
        trackCanvasFitView({ source: "keyboard" });
        rf.fitView({ duration: 300 });
        return;
      }

      if (
        event.key === "v" &&
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        canEdit
      ) {
        if (copyPaste.canPaste) {
          event.preventDefault();
          trackCanvasShortcutUsed({ shortcut: "paste" });
          copyPaste.paste();
          return;
        }
        // Don't preventDefault — let the native paste event fire so the paste
        // handler in useCopyPaste can access e.clipboardData (which includes
        // image files copied from Finder, unlike the async clipboard API).
        return;
      }

      if (event.key === "c" && (event.metaKey || event.ctrlKey)) {
        copyPaste.copy();
        return;
      }

      // Select all goes through React Flow's own selection so the change
      // flows as `select` NodeChanges (the only kind CanvasContext folds).
      // Left unhandled, Safari runs its document-wide Select All: the
      // resulting DOM range spans every node and WebKit paints selection
      // gaps over note bodies and the empty canvas. Respects the cursor
      // tool / placement modes the same way pane clicks do.
      if (event.key.toLowerCase() === "a" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "selectAll" });
        const store = storeApi.getState();
        if (!store.elementsSelectable) return;
        // addSelectedNodes selects whatever it is handed — unlike the marquee,
        // it never consults the node's own flags. Nodes that opted out
        // (server-written comment markers, the onboarding widget) are filtered
        // here so select-all can't hand them to Delete below. Locked nodes
        // stay out like they do from the marquee.
        store.addSelectedNodes(
          rf
            .getNodes()
            .filter(
              (n) => n.selectable !== false && !n.hidden && !isNodeLocked(n),
            )
            .map((n) => n.id),
        );
        return;
      }

      if (event.key === "d" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "duplicate" });
        copyPaste.duplicate();
        return;
      }

      if (event.key === "x" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        trackCanvasShortcutUsed({ shortcut: "cut" });
        copyPaste.cut();
        return;
      }

      if (!canEdit) return;

      if (CANVAS_KEYS.delete.includes(event.key)) {
        const nodes = rf.getNodes();
        const edges = rf.getEdges();
        const selectedEdges = edges.filter((e) => e.selected);
        if (
          selectedEdges.length > 0 &&
          nodes.filter((n) => n.selected).length === 0
        ) {
          event.preventDefault();
          trackCanvasShortcutUsed({ shortcut: "delete" });
          canvas.removeEdges(selectedEdges.map((e) => e.id));
          return;
        }
      }

      if (CANVAS_KEYS.delete.includes(event.key)) {
        const selectedNodes = rf
          .getNodes()
          .filter(
            (node) => (node as Node).selected && node.deletable !== false,
          );

        if (selectedNodes.length > 0) {
          event.preventDefault();
          trackCanvasShortcutUsed({ shortcut: "delete" });
          const nodeIds = selectedNodes.map((n) => n.id);
          const nodes = rf.getNodes();
          const detach = buildDetachUpdatesForSectionRemovals(
            nodeIds,
            nodes,
            rf.getInternalNode,
          );
          if (detach.length > 0) {
            canvas.batchUpdateNodeParents(detach);
          }
          canvas.removeNodes(nodeIds);

          // Two-meaning delete (D2): removing file nodes resolves their rows
          // through the shared board helper — link rows (matched by target
          // id too) remove the reference; canvas files archive; ids with no
          // canvas row are skipped so a workspace target is never archived by
          // accident.
          {
            const { canvasId } = navRef.current;
            const entityIds = collectFileEntityIds(selectedNodes as Node[]);
            if (canvasId && entityIds.length > 0) {
              void deleteFileRowsForRemovedNodes({
                queryClient: queryClientRef.current,
                canvasId,
                entityIds,
              });
            }
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
}
